import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Checkbox,
  FormGroup,
  //LinearProgress,
  Select, MenuItem, InputLabel, SelectChangeEvent
} from "@mui/material";
import { Buffer } from "buffer";
import React from "react";
import ReactHtmlParser from "react-html-parser";
import { Location, NavigateFunction } from "react-router-dom";
import SpeechClassComponent from "../QuestionSpeech/index";
import Header from "../SharedComponents/Header/Header";
import LoginGuard, { logout } from "../SharedComponents/LoginGuard/loginGuard";
import { withLocation } from "../SharedComponents/withLocation";
import { withNavigationHooks } from "../SharedComponents/withNavigationHooks";
import AudioRecorderAnswer from "./components/AudioRecorderAnswer";
import AttentionQuestion, {
  AttentionConfig,
  AttentionTaskAnswer,
} from "./components/AttentionQuestion";
import MathEffortQuestion, {
  MathEffortConfig,
  MathEffortTaskAnswer,
} from "./components/MathEffortQuestion";
import TowerQuestion, {
  TowerConfig,
  TowerTaskAnswer,
} from "./components/TowerQuestion";
import CategoriesQuestion, {
  CategoriesConfig,
  CategoriesTaskAnswer,
} from "./components/CategoriesQuestion";
import GrammarQuestion, {
  GrammarConfig,
  GrammarTaskAnswer,
} from "./components/GrammarQuestion";
import {
  IQuestion,
  QuestionsController,
  QuestionsControllerDelegate,
} from "./controller";

interface State {
  error: {
    invalidFreeText: boolean;
    message?: string;
  };
  data: {
    assignmentToken: string;
    answeredConsent: string;
    question?: IQuestion;
    selectedOptions: number[];
    freeTextAnswer?: string;
    towerTaskAnswer?: TowerTaskAnswer;
    attentionTaskAnswer?: AttentionTaskAnswer;
    mathEffortTaskAnswer?: MathEffortTaskAnswer;
    categoriesTaskAnswer?: CategoriesTaskAnswer;
    grammarTaskAnswer?: GrammarTaskAnswer;
    instrumentId: string;
  };
  isLoading: boolean;
  recordedAudio?: Blob;
}

interface Props {
  locationHooks: Location;
  navigationHooks: NavigateFunction;
}

class Questions
  extends React.Component<Props, State>
  implements QuestionsControllerDelegate {
  private controller: QuestionsController;
  private submitButtonRef: React.RefObject<HTMLButtonElement>;

  constructor(props: Props) {
    super(props);
    this.controller = new QuestionsController(this);
    this.submitButtonRef = React.createRef();
    this.state = {
      error: {
        invalidFreeText: false,
      },
      data: {
        assignmentToken: this.props.locationHooks.state.data.assignmentToken,
        answeredConsent: this.props.locationHooks.state.data.answeredConsent,
        instrumentId: this.props.locationHooks.state.data.instrumentId,
        selectedOptions: [],
      },
      isLoading: true,
      recordedAudio: undefined,
    };
  }

  componentDidMount(): void {
    this.loadPage();
  }

  componentDidUpdate(prevProps: Props, prevState: State): void {
    const currentQuestion = this.state.data.question;
    const didCompleteTowerNow =
      currentQuestion !== undefined &&
      this.isTowerQuestion(currentQuestion.type, currentQuestion.pattern) &&
      !prevState.data.towerTaskAnswer &&
      Boolean(this.state.data.towerTaskAnswer);
    const isLastTowerRound =
      currentQuestion !== undefined &&
      this.isTowerRoundPositionLast(currentQuestion);

    if (didCompleteTowerNow && !isLastTowerRound) {
      this.didClickSubmit();
      return;
    }

    const prevIsSubmitEnabled = this.getIsSubmitEnabled(prevState);
    const currentIsSubmitEnabled = this.getIsSubmitEnabled(this.state);

    if (!prevIsSubmitEnabled && currentIsSubmitEnabled) {
      // Focus
      const shouldAutoFocus = this.shouldAutoFocusButton();
      if (shouldAutoFocus) {
        setTimeout(() => {
          this.submitButtonRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          this.submitButtonRef.current?.focus({ preventScroll: true });
        }, 100);
      }
    }
  }

  // Auto focus condition
  private shouldAutoFocusButton(): boolean {
    const question = this.state.data.question;
    if (!question) return false;

    const questionType = question.type;
    const pattern = question.pattern;

    if (this.isTowerQuestion(questionType, pattern)) {
      return false;
    }
    if (this.isAttentionQuestion(questionType, pattern)) {
      return false;
    }
    if (this.isMathEffortQuestion(questionType, pattern)) {
      return false;
    }
    if (this.isCategoriesQuestion(questionType, pattern)) {
      return false;
    }
    if (this.isGrammarQuestion(questionType, pattern)) {
      return false;
    }
    if (questionType === "free_text") {
      return false;
    }
    if (
      (questionType === "categorical") &&
      pattern === "multiple_answer"
    ) {
      return false;
    }
    return true;
  }

  private isTowerQuestion(questionType: string, pattern: string): boolean {
    return pattern === "task" && questionType === "tower";
  }

  private isAttentionQuestion(questionType: string, pattern: string): boolean {
    return pattern === "task" && questionType === "sart";
  }

  private isMathEffortQuestion(questionType: string, pattern: string): boolean {
    return pattern === "task" && questionType === "math_effort";
  }

  private isCategoriesQuestion(questionType: string, pattern: string): boolean {
    return pattern === "task" && questionType === "category_learning";
  }

  private isGrammarQuestion(questionType: string, pattern: string): boolean {
    return pattern === "task" && questionType === "grammar";
  }

  private isTowerRoundPositionLast(question: IQuestion): boolean {
    const towerConfig = this.getTowerConfig(question);
    const roundPosition = towerConfig.round_position;

    if (typeof roundPosition !== "string") return false;
    const normalized = roundPosition.trim().toLowerCase();
    return normalized === "last" || normalized === "training_finish";
  }

  private getTowerConfig(question: IQuestion): Partial<TowerConfig> {
    const questionConfig = question.question_config;

    if (
      questionConfig &&
      typeof questionConfig === "object" &&
      !Array.isArray(questionConfig)
    ) {
      return questionConfig as Partial<TowerConfig>;
    }

    return {};
  }

  private getAttentionConfig(question: IQuestion): Partial<AttentionConfig> {
    const questionConfig = question.question_config;

    if (
      questionConfig &&
      typeof questionConfig === "object" &&
      !Array.isArray(questionConfig)
    ) {
      return questionConfig as Partial<AttentionConfig>;
    }

    return {};
  }

  private getCategoriesConfig(question: IQuestion): Partial<CategoriesConfig> {
    const questionConfig = question.question_config;
    if (
      questionConfig &&
      typeof questionConfig === "object" &&
      !Array.isArray(questionConfig)
    ) {
      return questionConfig as Partial<CategoriesConfig>;
    }
    return {};
  }

  private getMathEffortConfig(question: IQuestion): Partial<MathEffortConfig> {
    const questionConfig = question.question_config;

    if (
      questionConfig &&
      typeof questionConfig === "object" &&
      !Array.isArray(questionConfig)
    ) {
      return questionConfig as Partial<MathEffortConfig>;
    }

    return {};
  }

  // isSubmitEnabled
  private getIsSubmitEnabled(state: State): boolean {
    if (state.isLoading || !state.data.question) {
      return false;
    }

    const questionType = state.data.question.type;
    const pattern = state.data.question.pattern;

    if (questionType === "audio" && pattern === "single_answer") {
      return Boolean(state.recordedAudio && state.recordedAudio.size > 0);
    } else if (this.isCategoriesQuestion(questionType, pattern)) {
      return Boolean(state.data.categoriesTaskAnswer);
    } else if (this.isGrammarQuestion(questionType, pattern)) {
      return Boolean(state.data.grammarTaskAnswer);
    } else if (this.isMathEffortQuestion(questionType, pattern)) {
      return Boolean(state.data.mathEffortTaskAnswer);
    } else if (this.isAttentionQuestion(questionType, pattern)) {
      return Boolean(state.data.attentionTaskAnswer);
    } else if (this.isTowerQuestion(questionType, pattern)) {
      return Boolean(state.data.towerTaskAnswer);
    } else if (
      questionType === "polytomic" ||
      questionType === "dichotomic" ||
      questionType === "categorical"
    ) {
      return state.data.selectedOptions.length > 0;
    } else if (questionType === "free_text") {
      return !state.error.invalidFreeText && (state.data.freeTextAnswer?.length || 0) > 0;
    }

    return false;
  }

  // private linearProgressWithLabel(value: number) {
  //   return (
  //     <Box display="flex" alignItems="center" gap={2}>
  //       <Box flex={1}>
  //         <LinearProgress
  //           variant="determinate"
  //           value={value}
  //           sx={{
  //             height: 10,
  //             borderRadius: 6,
  //             backgroundColor: (theme) => theme.palette.grey[300],
  //             "& .MuiLinearProgress-bar": {
  //               borderRadius: 6,
  //             },
  //           }}
  //         />
  //       </Box>
  //       <Box>
  //         <Typography variant="body2" color="textSecondary">
  //           {`${value?.toFixed(1)}%`}
  //         </Typography>
  //       </Box>
  //     </Box>
  //   );
  // }

  // Preparation
  loadPage() {
    this.setState(
      {
        error: {
          invalidFreeText: false,
        },
        data: {
          ...this.state.data,
          selectedOptions: [],
          question: undefined,
          freeTextAnswer: undefined,
          towerTaskAnswer: undefined,
          attentionTaskAnswer: undefined,
          mathEffortTaskAnswer: undefined,
          categoriesTaskAnswer: undefined,
          grammarTaskAnswer: undefined,
        },
        isLoading: true,
        recordedAudio: undefined,
      },
      () => {
        this.controller.getNextQuestion(
          localStorage.getItem("AccessToken")!,
          this.state.data.assignmentToken,
          this.props.locationHooks.state.data.type
        );
      }
    );
  }

  // Delegate
  genericError(message?: string): void {
    this.setState((prevState) => ({
      error: {
        ...prevState.error,
        message:
          message ||
          "Não foi possível enviar sua resposta. Por favor, tente novamente.",
      },
    }));
  }

  applicationShouldLogout(): void {
    logout();
  }

  didEndInstrument(): void {
    const token = this.state.data.assignmentToken;
    const splitToken = token.split(".")[1];
    const tokenData = JSON.parse(
      Buffer.from(splitToken, "base64").toString("utf8")
    );
    const assignmentId = tokenData.assignmentId;

    const mountedStateData = {
      assignmentId: assignmentId,
      applicationId: this.props.locationHooks.state.data.applicationId,
      instrumentTitle: this.props.locationHooks.state.data.instrumentTitle,
      instrumentId: this.props.locationHooks.state.data.instrumentId,
      firstTimeFinished: true,
    };
    this.props.navigationHooks("/instrument/end", {
      state: { data: mountedStateData },
    });
  }

  didGetQuestion(question: IQuestion): void {
    let selectedOptions: number[] = [];
    let freeTextAnswer: string | undefined = undefined;
    if (question.type === "free_text") {
      freeTextAnswer = question.answers.length
        ? question.answers[0].text
        : undefined;
    } else {
      selectedOptions = question.answers
        .filter((answer) => answer.selected)
        .map((answer) => answer.order);
    }
    this.setState({
      data: {
        ...this.state.data,
        question,
        selectedOptions,
        freeTextAnswer,
        towerTaskAnswer: undefined,
        attentionTaskAnswer: undefined,
        mathEffortTaskAnswer: undefined,
        categoriesTaskAnswer: undefined,
      },
      isLoading: false,
    });
  }

  didSaveAnswer(): void {
    this.loadPage();
  }

  renderLoading() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  renderErrorAlert() {
    if (!this.state.error.message) {
      return null;
    }

    return (
      <Box
        width="100%"
        maxWidth={600}
        marginBottom={3}
        sx={{ alignSelf: "center" }}
      >
        <Alert severity="error">{this.state.error.message}</Alert>
      </Box>
    );
  }

  // Internals
  didClickSubmit() {
    const question = this.state.data.question;
    if (!question) return;

    const questionType = question.type;
    const pattern = question.pattern;

    if (questionType === "audio") {
      this.controller.sendAudioAnswer(
        localStorage.getItem("AccessToken")!,
        this.state.data.assignmentToken,
        question.order,
        this.state.recordedAudio!
      );
    } else if (this.isMathEffortQuestion(questionType, pattern)) {
      if (!this.state.data.mathEffortTaskAnswer) {
        return;
      }

      this.controller.sendTaskAnswer(
        localStorage.getItem("AccessToken")!,
        this.state.data.assignmentToken,
        question.order,
        this.state.data.mathEffortTaskAnswer
      );
    } else if (this.isAttentionQuestion(questionType, pattern)) {
      if (!this.state.data.attentionTaskAnswer) {
        return;
      }

      this.controller.sendTaskAnswer(
        localStorage.getItem("AccessToken")!,
        this.state.data.assignmentToken,
        question.order,
        this.state.data.attentionTaskAnswer
      );
    } else if (this.isCategoriesQuestion(questionType, pattern)) {
      if (!this.state.data.categoriesTaskAnswer) {
        return;
      }
      this.controller.sendTaskAnswer(
        localStorage.getItem("AccessToken")!,
        this.state.data.assignmentToken,
        question.order,
        this.state.data.categoriesTaskAnswer
      );
    } else if (this.isGrammarQuestion(questionType, pattern)) {
      if (!this.state.data.grammarTaskAnswer) {
        return;
      }
      this.controller.sendTaskAnswer(
        localStorage.getItem("AccessToken")!,
        this.state.data.assignmentToken,
        question.order,
        this.state.data.grammarTaskAnswer
      );
    } else if (this.isTowerQuestion(questionType, pattern)) {
      if (!this.state.data.towerTaskAnswer) {
        return;
      }

      this.controller.sendTaskAnswer(
        localStorage.getItem("AccessToken")!,
        this.state.data.assignmentToken,
        question.order,
        this.state.data.towerTaskAnswer
      );
    } else if (questionType === "free_text") {
      this.controller.sendFreeTextAnswer(
        localStorage.getItem("AccessToken")!,
        this.state.data.assignmentToken,
        question.order,
        this.state.data.freeTextAnswer || ""
      );
    } else {
      this.controller.sendAnswer(
        localStorage.getItem("AccessToken")!,
        this.state.data.assignmentToken,
        question.order,
        this.state.data.selectedOptions
      );
    }
  }

  private onTowerAnswerChange = (towerTaskAnswer?: TowerTaskAnswer) => {
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        towerTaskAnswer,
      },
    }));
  };

  private onAttentionAnswerChange = (attentionTaskAnswer?: AttentionTaskAnswer) => {
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        attentionTaskAnswer,
      },
    }));
  };

  private onCategoriesAnswerChange = (categoriesTaskAnswer?: CategoriesTaskAnswer) => {
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        categoriesTaskAnswer,
      },
    }));
  };

  private onGrammarAnswerChange = (grammarTaskAnswer?: GrammarTaskAnswer) => {
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        grammarTaskAnswer,
      },
    }));
  };

  private getGrammarConfig(question: IQuestion): Partial<GrammarConfig> {
    const questionConfig = question.question_config;
    if (
      questionConfig &&
      typeof questionConfig === "object" &&
      !Array.isArray(questionConfig)
    ) {
      return questionConfig as Partial<GrammarConfig>;
    }
    return {};
  }

  private onMathEffortAnswerChange = (mathEffortTaskAnswer?: MathEffortTaskAnswer) => {
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        mathEffortTaskAnswer,
      },
    }));
  };

  // Radio question
  setRadioAnswer(event: React.ChangeEvent<HTMLInputElement>) {
    this.setState({
      data: {
        ...this.state.data,
        selectedOptions: [Number.parseInt(event.target.value)],
      },
    });
  }

  renderRadio() {
    const answers = this.state.data.question?.answers;
    let defaultSelected = null;
    const sortedAnswers = answers?.sort((a, b) => a.order - b.order);
    const options = sortedAnswers?.map((answer) => {
      const audio = this.renderSpeechTextAnswer(answer?.description, false, {
        display: "inline",
      });
      if (answer.selected) {
        defaultSelected = answer.order;
      }
      return (
        <FormControlLabel
          key={answer.order}
          value={answer.order}
          control={<Radio />}
          sx={{
            alignItems: 'flex-start',
            '.MuiTypography-root': {
              marginTop: '4px',
            }
          }}
          label={
            <Typography variant="h6" color="primary">
              {answer.description}
              {answer.allow_audio ? audio : null}
            </Typography>
          }
        />
      );
    });
    return (
      <Box
        alignSelf="flex-center"
        marginTop={6}
        sx={{
          backgroundColor: 'grey.100', borderRadius: '6px',
          padding: {
            xs: '1rem 1rem',
            md: '1.25rem 2rem',
          },
          minWidth: {
            xs: '0px',
            md: '400px'
          },
          wordBreak: 'break-word'
        }}>
        <FormControl>
          <RadioGroup
            onChange={(event) => this.setRadioAnswer(event)}
            defaultValue={defaultSelected}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}
          >
            {options}
          </RadioGroup>
        </FormControl>
      </Box>
    );
  }

  // Checkbox question
  setCheckboxesAnswer(event: React.ChangeEvent<HTMLInputElement>, answerOrder: number) {
    const isChecked = event.target.checked;
    const currentSelectedOptions = this.state.data.selectedOptions || [];

    let newSelectedOptions;

    if (isChecked) {
      // Add the option
      if (!currentSelectedOptions.includes(answerOrder)) {
        newSelectedOptions = [...currentSelectedOptions, answerOrder];
      } else {
        newSelectedOptions = currentSelectedOptions;
      }
    } else {
      // Remove the option
      newSelectedOptions = currentSelectedOptions.filter(option => option !== answerOrder);
    }

    this.setState({
      data: {
        ...this.state.data,
        selectedOptions: newSelectedOptions,
      },
    });
  }

  renderCheckboxes() {
    const answers = this.state.data.question?.answers;
    const sortedAnswers = answers?.sort((a, b) => a.order - b.order);

    const options = sortedAnswers?.map((answer) => {
      const audio = this.renderSpeechTextAnswer(answer?.description, false, {
        display: "inline",
      });

      return (
        <FormControlLabel
          key={answer.order}
          control={
            <Checkbox
              checked={this.state.data.selectedOptions?.includes(answer.order) || false}
              onChange={(event) => this.setCheckboxesAnswer(event, answer.order)}
              value={answer.order}
            />
          }
          sx={{
            alignItems: 'flex-start',
            '.MuiTypography-root': {
              marginTop: '4px',
            }
          }}
          label={
            <Typography variant="h6" color="primary">
              {answer.description}
              {answer.allow_audio ? audio : null}
            </Typography>
          }
        />
      );
    });

    return (
      <Box
        alignSelf="flex-center"

        marginTop={6}
        sx={{
          boxSizing: 'border-box',
          backgroundColor: 'grey.100', borderRadius: '6px',
          padding: {
            xs: '1rem 1rem',
            md: '1.25rem 2rem',
          },
          minWidth: {
            xs: '0px',
            md: '400px'
          },
          wordBreak: 'break-word'
        }}>
        <FormControl>
          <FormGroup
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
            {options}
          </FormGroup>
        </FormControl>
      </Box>
    );
  }

  // Select question
  setSelectAnswer(event: SelectChangeEvent<number>) {
    this.setState({
      data: {
        ...this.state.data,
        selectedOptions: [Number.parseInt(event.target.value.toString())],
      },
    });
  }

  renderSelect() {
    const answers = this.state.data.question?.answers;
    const sortedAnswers = answers?.sort((a, b) => a.order - b.order);

    let defaultSelected = '';
    const options = sortedAnswers?.map((answer) => {
      if (answer.selected) {
        defaultSelected = answer.order.toString();
      }
      return (
        <MenuItem key={answer.order} value={answer.order}>
          {answer.description}
        </MenuItem>
      );
    });

    return (
      <Box
        width="100%"
        alignSelf="flex-center"
        marginTop={6}
        sx={{
          display: 'flex',
          backgroundColor: 'grey.100', borderRadius: '6px',
          padding: {
            xs: '1rem 1rem',
            md: '1.25rem 1.25rem',
          },
          maxWidth: { xs: 300, sm: 400, md: 500, lg: 600 },
          boxSizing: 'border-box',
        }}
      >

        <FormControl sx={{ width: { xs: '100%' } }}>
          <InputLabel id="select-label">Selecione uma opção</InputLabel>
          <Select
            labelId="select-label"
            id="select-option"
            value={this.state.data.selectedOptions[0] || ''}
            label="Selecione uma opção"
            onChange={(event) => this.setSelectAnswer(event)}
          >
            {options}
          </Select>
        </FormControl>
      </Box>
    );
  }

  // Number sequence 1-n (Polytomic)
  setPolytomicAnswer(value: number) {
    return () => {
      this.setState({
        data: {
          ...this.state.data,
          selectedOptions: [value],
        },
      });
    };
  }

  renderPolitomicReminder() {
    const answers = this.state.data.question?.answers;

    const shouldShowReminders = answers?.reduce(
      (acc, cur) => acc && !cur.icon,
      true
    );
    const sortedAnswers = answers?.sort((a, b) => a.order - b.order);

    if (shouldShowReminders) {
      const labels = sortedAnswers?.map((answer, index) => {
        return (
          <Typography key={`label_${index}`} variant="subtitle1">
            {`${index + 1} - ${answer.description}`}
          </Typography>
        );
      });
      const textToSpeech = [
        "Lembrando que:",
        ...(sortedAnswers?.map(
          (answer, index) => `${index + 1} - ${answer.description}`
        ) || []),
      ];
      const audio = this.renderSpeechTextAnswer(
        textToSpeech?.join(", "),
        false
      );
      return (
        <Box alignSelf="flex-center"
          marginTop={6}
          sx={{
            backgroundColor: 'grey.100', borderRadius: '6px',
            padding: {
              xs: '1rem 1rem',
              md: '1.25rem 2rem',
            },
            minWidth: {
              xs: '0px',
              md: '400px'
            },
            wordBreak: 'break-word'
          }}>
          <Box >
            {this.state.data.question!.audio ? <>{audio}</> : null}
            <Typography variant="h6" color="primary">
              Lembrando que:
            </Typography>
            {labels}
          </Box>
        </Box>
      );
    }

    return null;
  }

  renderPolitomicAnswers() {
    const answers = this.state.data.question?.answers;
    const sortedAnswers = answers?.sort((a, b) => a.order - b.order);
    const iconSize = sortedAnswers?.some((answer, index) => {
      return answer.icon_size === "m" || answer.icon_size === "g";
    });
    const selected = this.state.data.selectedOptions;
    const options = sortedAnswers?.map((answer, index) => {
      const isSelected = selected.indexOf(answer.order) >= 0;
      // const audio = answer.allow_audio
      //   ? this.renderSpeechTextAnswer((index + 1).toString(), true)
      //   : null;
      const audioIcon = answer.allow_audio
        ? this.renderSpeechTextAnswer(answer.description, true)
        : null;
      return (
        <Button
          id="botao-tamanho"
          sx={{
            width:
              answer.icon_size === "m"
                ? 200
                : answer.icon_size === "g"
                  ? 300
                  : answer.icon_size === "p"
                    ? 100
                    : 50,
            height:
              answer.icon_size === "m"
                ? 200
                : answer.icon_size === "g"
                  ? 300
                  : answer.icon_size === "p"
                    ? 100
                    : 50,
            margin: "3px 0",
            border: "1px solid #1ea097 !important",
            borderRadius: "4px",
          }}
          onClick={(event) => this.setPolytomicAnswer(answer.order)()}
          key={index}
          variant={isSelected ? "contained" : "outlined"}
        >
          {answer.icon ? (
            <>
              {isSelected ? audioIcon : null}
              <Box
                component="img"
                src={answer.icon}
                sx={{
                  width: "90%",
                  height: "90%",
                  objectFit: "cover",
                  position: "absolute",
                }}
              />
            </>
          ) : (
            <>
             
              <Typography variant="h5" color={isSelected ? "white" : "primary"}>
                {index + 1}
              </Typography>
            </>
          )}
        </Button>
      );
    });

    return (
      <React.Fragment>
        <Box marginTop={3}>
          <ButtonGroup
            sx={{
              flexWrap: "wrap",
              justifyContent: "center",
              ...(iconSize && {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",

              }),
            }}
            variant="outlined"
          >
            {options}
          </ButtonGroup>
        </Box>
      </React.Fragment>
    );
  }

  // Free text - short and long
  onChangeTextAnswer(
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) {
    const newData = {
      ...this.state.data,
      freeTextAnswer: event.target.value,
    };

    const newError = {
      ...this.state.error,
    };

    // validation
    if (this.state.data.question?.pattern === "numerical") {
      newError.invalidFreeText = !/^[0-9]+?$/.test(event.target.value);
    }

    this.setState({
      data: newData,
      error: newError,
    });
  }

  renderShortTextAnswer() {
    const answer = this.state.data.question!.answers[0];

    return (
      <React.Fragment>
        <Box width="100%"
          alignSelf="flex-center"
          marginTop={6}
          sx={{
            display: 'flex',
            backgroundColor: 'grey.100', borderRadius: '6px',
            padding: {
              xs: '1rem 1rem',
              md: '1.25rem 1.25rem',
            },
            maxWidth: { xs: 300, sm: 400, md: 500, lg: 600 },
            boxSizing: 'border-box',
          }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <TextField
              sx={{ width: { xs: '100%' } }}
              label={answer.description}
              error={this.state.error.invalidFreeText}
              onChange={(event) => this.onChangeTextAnswer(event)}
              inputProps={{ maxLength: 240 }}
              variant="outlined"
            />
            <Typography variant="caption" align="right" sx={{ mt: 0.5, color: 'text.secondary' }}>
              {(this.state.data.freeTextAnswer ?? '').length}/240
            </Typography>
          </Box>
        </Box>
      </React.Fragment>
    );
  }

  renderLongTextAnswer() {
    const answer = this.state.data.question!.answers[0];

    return (
      <React.Fragment>
        <Box width="100%"
          alignSelf="flex-center"
          marginTop={6}
          sx={{
            display: 'flex',
            backgroundColor: 'grey.100', borderRadius: '6px',
            padding: {
              xs: '1rem 1rem',
              md: '1.25rem 1.25rem',
            },
            maxWidth: { xs: 300, sm: 400, md: 500, lg: 600 },
            boxSizing: 'border-box',
          }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <TextField
              label={answer.description}
              multiline
              rows={10}
              fullWidth
              variant="outlined"
              value={this.state.data.freeTextAnswer || ''}
              onChange={(event) => this.onChangeTextAnswer(event)}
              placeholder="Digite sua resposta aqui..."
              inputProps={{ maxLength: 5000 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  alignItems: 'flex-start',
                },
              }}
            />
            <Typography variant="caption" align="right" sx={{ mt: 0.5, color: 'text.secondary' }}>
              {(this.state.data.freeTextAnswer ?? '').length}/5000
            </Typography>
          </Box>
        </Box>
      </React.Fragment>
    );
  }

  renderSpeechTextAnswer(text: string, auto?: boolean, css?: object | null) {
    return (
      <React.Fragment>
        <SpeechClassComponent text={text} auto={auto} css={css} />
      </React.Fragment>
    );
  }

  // Question render cases
  renderQuestionCases() {
    //const currentQuestion = this.state.data.question?.order;
    //const totalQuestions = this.props.locationHooks.state.data.totalQuestions;
    const instrumentTitle = this.props.locationHooks.state.data.instrumentTitle;

    const questionType = this.state.data.question!.type;
    const pattern = this.state.data.question!.pattern;
    const isTowerQuestionType = this.isTowerQuestion(questionType, pattern);
    const isAttentionQuestionType = this.isAttentionQuestion(questionType, pattern);
    const isMathEffortQuestionType = this.isMathEffortQuestion(questionType, pattern);
    const isCategoriesQuestionType = this.isCategoriesQuestion(questionType, pattern);
    const isGrammarQuestionType = this.isGrammarQuestion(questionType, pattern);
    const audio = this.renderSpeechTextAnswer(
      this.state.data.question!.text_to_audio,
      false
    );

    let answers;
    let reminder = null;
    let isSubmitEnabled: boolean = false;

    if (questionType === "audio" && pattern === "single_answer") {
      answers = (
        <AudioRecorderAnswer
          onAudioRecorded={(audioBlob) => {
            this.setState({ recordedAudio: audioBlob });
          }}
        />
      );
      isSubmitEnabled = Boolean(
        this.state.recordedAudio && this.state.recordedAudio.size > 0
      );

    } else if (questionType === "polytomic") {
      reminder = this.renderPolitomicReminder();
      answers = this.renderPolitomicAnswers();
      isSubmitEnabled = this.state.data.selectedOptions.length > 0;

    } else if (questionType === "dichotomic") {
      answers = this.renderRadio();
      isSubmitEnabled = this.state.data.selectedOptions.length > 0;

    } else if (isTowerQuestionType) {
      const towerConfig = this.getTowerConfig(this.state.data.question!);
      answers = (
        <TowerQuestion
          key={`tower_${this.state.data.question!.order}`}
          onAnswerChange={this.onTowerAnswerChange}
          towerConfig={towerConfig}
        />
      );
      isSubmitEnabled = Boolean(this.state.data.towerTaskAnswer);

    } else if (isAttentionQuestionType) {
      const attentionConfig = this.getAttentionConfig(this.state.data.question!);
      answers = (
        <AttentionQuestion
          key={`attention_${this.state.data.question!.order}`}
          onAnswerChange={this.onAttentionAnswerChange}
          attentionConfig={attentionConfig}
        />
      );
      isSubmitEnabled = Boolean(this.state.data.attentionTaskAnswer);

    } else if (isMathEffortQuestionType) {
      const mathEffortConfig = this.getMathEffortConfig(this.state.data.question!);
      answers = (
        <MathEffortQuestion
          key={`math_effort_${this.state.data.question!.order}`}
          onAnswerChange={this.onMathEffortAnswerChange}
          mathEffortConfig={mathEffortConfig}
        />
      );
      isSubmitEnabled = Boolean(this.state.data.mathEffortTaskAnswer);

    } else if (isCategoriesQuestionType) {
      const categoriesConfig = this.getCategoriesConfig(this.state.data.question!);
      answers = (
        <CategoriesQuestion
          key={`categories_${this.state.data.question!.order}`}
          onAnswerChange={this.onCategoriesAnswerChange}
          categoriesConfig={categoriesConfig}
        />
      );
      isSubmitEnabled = Boolean(this.state.data.categoriesTaskAnswer);

    } else if (isGrammarQuestionType) {
      const grammarConfig = this.getGrammarConfig(this.state.data.question!);
      answers = (
        <GrammarQuestion
          key={`grammar_${this.state.data.question!.order}`}
          onAnswerChange={this.onGrammarAnswerChange}
          grammarConfig={grammarConfig}
        />
      );
      isSubmitEnabled = Boolean(this.state.data.grammarTaskAnswer);

    } else if (questionType === "categorical") {

      if (pattern === "single_answer") {
        answers = this.renderRadio();
      } else if (pattern === "multiple_answer") {
        answers = this.renderCheckboxes();
      } else if (pattern === "select_answer") {
        answers = this.renderSelect();
      }
      isSubmitEnabled = this.state.data.selectedOptions.length > 0;

    } else if (questionType === "free_text") {
      if (pattern === "short_text" || pattern === "numerical") {
        answers = this.renderShortTextAnswer();
      } else if (pattern === "long_text") {
        answers = this.renderLongTextAnswer();
      }
      isSubmitEnabled =
        !this.state.error.invalidFreeText &&
        (this.state.data.freeTextAnswer?.length || 0) > 0;
    }

    const shouldShowSubmitButton =
      (!isTowerQuestionType && !isAttentionQuestionType && !isMathEffortQuestionType && !isCategoriesQuestionType && !isGrammarQuestionType) ||
      isSubmitEnabled;

    return (
      <Box
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          padding: {
            xs: '2rem 1rem',
            md: '3rem 1.5rem',
          }
        }}
      >
        <Box
          alignItems="center"
          display="flex"
          flexDirection="column"
          sx={{
            maxWidth: '1200px',
            margin: '0 auto'
          }}
        >
          {/* {(this.props.locationHooks.state.data.type === 'linear' && currentQuestion) && (
            <Box
              width="100%"
              marginBottom={2}
              sx={{
                maxWidth: { xs: 300, sm: 400 },
              }}>
              {this.linearProgressWithLabel((currentQuestion / totalQuestions) * 100)}
            </Box>
          )} */}
          <Box marginBottom={3} sx={{ textAlign: 'center', fontSize: '1rem', color: 'primary.main', fontWeight: '500' }}>
            {instrumentTitle}
          </Box>
          {this.renderErrorAlert()}

          <Box style={{ fontSize: 24, textAlign: 'center' }}>
            {this.state.data.question!.audio ? audio : null}
            {ReactHtmlParser(this.state.data.question!.description)}
          </Box>
          {reminder}
          {answers}
          {shouldShowSubmitButton ? (
            <Box marginTop={6} width="100%" display="flex" justifyContent="center">
              <Button
                ref={this.submitButtonRef}
                variant="contained"
                disabled={!isSubmitEnabled}
                onClick={(event) => {
                  if (!isSubmitEnabled) return;
                  this.didClickSubmit();
                }}
                sx={{
                  borderRadius: 10,
                  width: { xs: '100%', sm: 300, md: 300 },
                  alignSelf: "center",
                }}
              >
                <Typography color="white" variant="button">
                  Próximo
                </Typography>
              </Button>
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }

  render() {
    return (
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        alignItems="center"
        justifyContent="center"
      >
        <LoginGuard />
        <Header logoutEnabled={true} />
        {this.state.isLoading ? this.renderLoading() : this.renderQuestionCases()}
      </Box>
    );
  }
}

export default withNavigationHooks(withLocation(Questions));
